import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import {
  useActiveConversationsList,
  useArchivedConversationsList,
  useConversationsList,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { cn } from '@avanta/shared/utils/cn';
import { Archive, Inbox, LogOut, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { useIsSupporter } from 'stores/user';
import { isEmotionalSupport } from 'utils/chat';
import { ConversationList } from './components/ConversationList';
import { EmptyListResults } from './components/EmptyListResults';
import { NewChat } from './components/NewChat';

export const Sidebar = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const conversations = useConversationsList();
  const activeConversations = useActiveConversationsList();
  const archivedConversations = useArchivedConversationsList();

  const [search, setSearch] = useState('');
  const [isOpenUserInfo, setIsOpenUserInfo] = useState(false);
  const [isOpenArchivedChats, setIsOpenArchivedChats] = useState(false);

  const isSupporter = useIsSupporter();
  const showNewConversationAction = isSupporter || isEmotionalSupport;

  const normalizedSearch = search.toLowerCase();
  const filteredConversations = conversations.filter(
    conversation => conversation.otherUser.name?.toLowerCase().includes(normalizedSearch) ?? false
  );
  const {
    isSelectionMode,
    selectedChannelIds,
    archiveSelection,
    unarchiveSelection,
    leaveSelection,
  } = useConversationsStore(
    useShallow(state => ({
      isSelectionMode: state.isSelectionMode,
      selectedChannelIds: state.selectedChannelIds,
      archiveSelection: state.archiveSelection,
      unarchiveSelection: state.unarchiveSelection,
      leaveSelection: state.leaveSelection,
    }))
  );
  const [isProcessingSelection, setIsProcessingSelection] = useState(false);
  const isDisabled = selectedChannelIds.size === 0 || isProcessingSelection;

  const renderContent = (): React.ReactElement => {
    if (search.length > 0 && filteredConversations.length === 0) {
      return <EmptyListResults mode="search" />;
    }
    if (search.length > 0) {
      return (
        <ConversationList
          conversations={filteredConversations}
          title={t('Chat.SideBar.chats.heading')}
        />
      );
    }
    if (isOpenArchivedChats) {
      if (archivedConversations.length === 0) {
        return <EmptyListResults mode="archived" />;
      }
      return (
        <ConversationList
          conversations={archivedConversations}
          title={t('Chat.SideBar.archivedChats.heading')}
        />
      );
    }
    if (activeConversations.length === 0) {
      return <EmptyListResults mode="active" />;
    }
    return (
      <ConversationList
        conversations={activeConversations}
        title={t('Chat.SideBar.chats.heading')}
      />
    );
  };

  const handleToggleArchivedChats = (): void => {
    setIsOpenArchivedChats(prev => !prev);
  };

  const handleArchiveOrUnarchive = async (): Promise<void> => {
    setIsProcessingSelection(true);
    try {
      if (isOpenArchivedChats) {
        await unarchiveSelection(selectedChannelIds);
        toast.success(t('Chat.unarchiveAll.success'));
      } else {
        await archiveSelection(selectedChannelIds);
        toast.success(t('Chat.archiveAll.success'));
      }
    } finally {
      setIsProcessingSelection(false);
    }
  };

  const handleLeaveSelection = async (): Promise<void> => {
    setIsProcessingSelection(true);
    try {
      await leaveSelection(selectedChannelIds);
      toast.success(t('Chat.leaveAll.success'));
    } finally {
      setIsProcessingSelection(false);
    }
  };

  const handleOnCloseNewChat = (): void => {
    setIsOpenUserInfo(false);
    setIsOpenArchivedChats(false);
    setIsProcessingSelection(false);
    setSearch('');
  };

  const renderActionButtons = (): React.ReactElement => {
    if (isSelectionMode) {
      return (
        <div className="flex gap-2 w-full mt-2">
          <Button
            variant="terciary"
            icon="leading"
            size="md"
            className="w-1/2"
            onClick={handleArchiveOrUnarchive}
            disabled={isDisabled}
          >
            {isOpenArchivedChats ? <Inbox /> : <Archive />}
            <span>{isOpenArchivedChats ? t('Chat.unarchive.cta') : t('Chat.archive.cta')}</span>
          </Button>
          <Button
            variant="terciary"
            icon="leading"
            size="md"
            className="w-1/2"
            onClick={handleLeaveSelection}
            disabled={isDisabled}
          >
            <LogOut />
            <span>{t('Chat.leave.cta')}</span>
          </Button>
        </div>
      );
    }
    return (
      <Button
        variant="terciary"
        icon="leading"
        size="md"
        className="mt-2 w-full"
        onClick={handleToggleArchivedChats}
      >
        {isOpenArchivedChats ? <Inbox /> : <Archive />}
        <span>
          {isOpenArchivedChats
            ? t('Chat.SideBar.activeChats.heading')
            : t('Chat.SideBar.archivedChats.heading')}
        </span>
      </Button>
    );
  };

  return (
    <div
      className={cn(
        'flex flex-col w-full flex-1 am-xl:w-[320px] am-xl:flex-none',
        'bg-white pb-20 border-t border-l border-b border-gray-200 rounded-l-lg'
      )}
    >
      <div className="flex gap-2 items-center justify-between p-4">
        <Input
          placeholder={t('Chat.SideBar.search.placeholder')}
          leftIcon={Search}
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        {showNewConversationAction && (
          <Button variant="terciary" icon="only" onClick={() => setIsOpenUserInfo(true)}>
            <Plus />
          </Button>
        )}
      </div>
      <div className="px-4 overflow-auto">
        {renderContent()}
        {renderActionButtons()}
      </div>
      {isOpenUserInfo && <NewChat onClose={handleOnCloseNewChat} />}
    </div>
  );
};
